import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import Icon from '@/components/common/iconedLinks/Icon';
import FullScreenContainerV2 from '@/components/layout/modals/FullScreenContainerV2';
import { ModalHeaderDsProps } from '@/components/layout/modals/ModalHeader.ds';
import { NS } from '@/constants';
import { ANIMATION } from '@/constants/animation';
import { URLS } from '@/constants/urls';
import { useAnimation } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux';
import { selectBlogMenu } from '@/redux/slices/blog.slice';
import { toggleBlogMenuModal, toggleBlogSubmenuModal } from '@/redux/slices/gui.slice';
import { MenuItem } from '@/types';
import styles from './BlogMenuModal.module.scss';

const animationMenuModal = {
	opening: ANIMATION.FROM_LEFT,
	closing: ANIMATION.TO_LEFT,
	openingMobile: ANIMATION.FROM_LEFT,
	closingMobile: ANIMATION.TO_LEFT,
};

export const BlogMenuModal = (): React.JSX.Element => {
	const { t } = useTranslation(NS.COMMON);
	const dispatch = useAppDispatch();
	const blogMenu = useAppSelector(selectBlogMenu);
	const blogSubmenuModalVisible = useAppSelector(({ gui }) => gui.blogSubmenuModalVisible);
	const blogMenuModalVisible = useAppSelector(({ gui }) => gui.blogMenuModalVisible);
	const { animationState: animation, handleCloseModal } = useAnimation(animationMenuModal);
	const {
		animationState: animationSecond,
		handleCloseModal: handleCloseSecondModal,
		resetSecondModal,
	} = useAnimation(animationMenuModal);
	const [selectedMenu, setSelectedMenu] = useState(null);

	const closeModal = () => {
		handleCloseModal();
		setTimeout(() => {
			dispatch(toggleBlogSubmenuModal({ show: false }));
			dispatch(toggleBlogMenuModal({ show: false }));
		}, ANIMATION.DELAY_MODAL);
	};

	const closeSecondModal = () => {
		handleCloseSecondModal();
		setTimeout(() => {
			dispatch(toggleBlogSubmenuModal({ show: false }));
		}, ANIMATION.DELAY_MODAL);
	};

	const firstModalHeader = {
		onCloseClick: closeModal,
		centeredTitle: true,
		title: 'MILLESIMA',
	} satisfies ModalHeaderDsProps;

	const secondModalHeader = {
		whichButton: 'bothButtons',
		onReturnClick: closeSecondModal,
		onCloseClick: closeModal,
		title: blogMenu[selectedMenu]?.title,
	} satisfies ModalHeaderDsProps;

	const handleItemClick = (index) => {
		if (resetSecondModal) {
			resetSecondModal();
		}
		setSelectedMenu(index);
		dispatch(toggleBlogSubmenuModal({ show: true }));
	};

	const getSubmenuComponent = ({ type, subMenus, subLinks }: MenuItem) => {
		switch (type) {
			case 'LINKS':
				return subMenus.map(({ title, subLinks }) => {
					return (
						<div className={styles['sub-menu']} key={title}>
							<li className={`${styles.item} ${styles['second-menu']}`}>{title}</li>
							{subLinks.map(({ label, link }) => (
								<a key={label} href={link}>
									<li className={styles.subitem}>{label}</li>
								</a>
							))}
						</div>
					);
				});
			case 'IMAGES':
				return subLinks.map(({ label, link }) => (
					<a key={label} href={link}>
						<li className={styles.subitem}>{label}</li>
					</a>
				));
			default:
		}
	};

	return (
		<>
			<FullScreenContainerV2
				onClose={closeModal}
				overlay
				classNames={{ container: styles.container }}
				animationClass={animation}
				isOpen={blogMenuModalVisible}
				designSystemHeader={firstModalHeader}
				padding="none"
			>
				<ul className={styles.list}>
					{blogMenu?.map(({ title }, index) => (
						<li className={styles.item} key={title} onClick={() => handleItemClick(index)}>
							<span>{title}</span>
							<Icon src={URLS.ANGLE_RIGHT_BLACK} alt={t('angleRightIcon', { ns: NS.ALTER })} width={24} height={24} />
						</li>
					))}
				</ul>
			</FullScreenContainerV2>
			{blogSubmenuModalVisible && (
				<FullScreenContainerV2
					onClose={closeModal}
					overlay
					classNames={{ container: styles.container }}
					animationClass={animationSecond}
					isOpen={blogSubmenuModalVisible}
					designSystemHeader={secondModalHeader}
					padding="none"
				>
					<ul className={styles.list}>{getSubmenuComponent(blogMenu[selectedMenu])}</ul>
				</FullScreenContainerV2>
			)}
		</>
	);
};

export default BlogMenuModal;
